import { api, apiClient } from "../lib";
import type { DateOnlyString } from "../utils/dateOnly";
import type { AccessionLogPrefixOption } from "../types/accessionLogPrefix";

// ─── Shared Types ─────────────────────────────────────────────────────────────

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─── Workload ─────────────────────────────────────────────────────────────────

export type WorkflowStage =
  | "accessioning"
  | "grossing"
  | "embedding"
  | "cutting"
  | "distribution"
  | "pathologist_review";

export interface WorkloadParams {
  stage: WorkflowStage;
  startDate: DateOnlyString;
  endDate: DateOnlyString;
  userIds?: string;
  facilityId?: string;
  page?: number;
  pageSize?: number;
}

export interface WorkloadUserRow {
  userId: string;
  userName: string;
  avgAccessioningMinutes?: number | null;
  accessioningRatePerHour?: number | null;
  casesAccessioned?: number;
  specimensAccessioned?: number;
  casesGrossed?: number;
  specimensGrossed?: number;
  avgGrossingMinutes?: number | null;
  medianGrossingMinutes?: number | null;
  casesEmbedded?: number;
  specimensEmbedded?: number;
  blocksEmbedded?: number;
  avgEmbeddingMinutes?: number | null;
  medianEmbeddingMinutes?: number | null;
  casesCut?: number;
  blocksCut?: number;
  slidesCut?: number;
  casesDistributed?: number;
  slidesDistributed?: number;
  casesSigned?: number;
  slidesReviewed?: number;
  cptCodesAssigned?: number;
  avgReviewMinutes?: number | null;
  medianReviewMinutes?: number | null;
  avgTatDays?: number | null;
  medianTatDays?: number | null;
}

export interface WorkloadReportResponse {
  stage: WorkflowStage;
  startDate: string;
  endDate: string;
  data: WorkloadUserRow[];
  pagination: PaginationMeta;
}

// ─── CPT Frequency ────────────────────────────────────────────────────────────

export type BillingReportDateBasis =
  | "first_reported"
  | "date_of_service"
  | "date_received";
export type AccessionLogDateBasis = BillingReportDateBasis | "date_accessioned";
export type BillingReportAccountType =
  | "practice"
  | "surgery_center"
  | "sending_lab";
export const DEFAULT_BILLING_REPORT_ACCOUNT_TYPE: BillingReportAccountType =
  "practice";

export interface CptFrequencyParams {
  startDate: string;
  endDate: string;
  dateBasis: BillingReportDateBasis;
  accountTypes: BillingReportAccountType[];
  pathologistId?: string;
  practiceId?: string;
  billingScenarioId?: string;
  prefixId?: string;
  physicianId?: string;
  surgeryCenterLabId?: string;
  page?: number;
  pageSize?: number;
  reportRunId?: string;
}

export interface CptFrequencyRow {
  cptCodeId: string;
  cptCode: string;
  description: string;
  count: number;
  unitFee: number | null;
  totalFee: number | null;
  stainClassification: "Additional stain" | "Regular stain" | "";
}

export interface CptByPracticeRow {
  practiceId: string;
  practiceName: string;
  totalCptCount: number;
  breakdown: CptFrequencyRow[];
}

export interface CptByPhysicianRow {
  physicianId: string;
  physicianName: string;
  totalCptCount: number;
  breakdown: CptFrequencyRow[];
}

export interface CptBySurgeryCenterRow {
  surgeryCenterLabId: string;
  surgeryCenterName: string;
  totalCptCount: number;
  breakdown: CptFrequencyRow[];
}

export interface CptByAccountPhysicianRow {
  accountType: BillingReportAccountType | "unassigned";
  accountId: string;
  accountName: string;
  physicianId: string;
  physicianName: string;
  prefixId: string;
  prefixCode: string;
  cptCode: string;
  description: string;
  count: number;
  stainClassification: "Additional stain" | "Regular stain" | "";
}

export interface CptByPrefixRow {
  id: string;
  name: string;
  prefixId: string;
  prefixCode: string;
  totalCptCount: number;
  breakdown: CptFrequencyRow[];
}

export interface CptFrequencyReportResponse {
  generatedAt: string;
  reportRunId: string;
  grandTotal: number;
  unassignedCaseCount: number;
  backfilledTcCaseCount: number;
  reconciliationCases: BillingReportReconciliationCase[];
  byCode: CptFrequencyRow[];
  allByCode?: CptFrequencyRow[];
  byPractice: CptByPracticeRow[];
  byPhysician: CptByPhysicianRow[];
  bySurgeryCenter: CptBySurgeryCenterRow[];
  byPrefix: CptByPrefixRow[];
  byAccountPhysician: CptByAccountPhysicianRow[];
  pagination: PaginationMeta;
}

export interface CptFrequencyExportData {
  generatedAt: string;
  startDate: string;
  endDate: string;
  dateBasis: BillingReportDateBasis;
  accountTypes: BillingReportAccountType[];
  activeFilters: BillingReportFilterLabel[];
  grandTotal: number;
  unassignedCaseCount: number;
  backfilledTcCaseCount: number;
  reconciliationCases: BillingReportReconciliationCase[];
  rows: CptByAccountPhysicianRow[];
  summary: CptFrequencyRow[];
}

export interface BillingReportFilterLabel {
  label: string;
  value: string;
}

export interface BillingReportReconciliationCase {
  caseId: string;
  caseNumber: string;
  issues: Array<
    "Unassigned billing account" | "TC snapshot initialized by report"
  >;
}

export type SpecimenLogParams = Omit<CptFrequencyParams, "page" | "pageSize">;

export interface SpecimenLogRow {
  accountType: BillingReportAccountType | "unassigned";
  accountId: string;
  accountName: string;
  caseCount: number;
  partCount: number;
}

export interface SpecimenLogResponse {
  generatedAt: string;
  reportRunId?: string;
  startDate: string;
  endDate: string;
  dateBasis: BillingReportDateBasis;
  accountTypes: BillingReportAccountType[];
  activeFilters: BillingReportFilterLabel[];
  rows: SpecimenLogRow[];
  totalCases: number;
  totalParts: number;
  unassignedCaseCount: number;
  reconciliationCases: BillingReportReconciliationCase[];
}

export interface AccessionLogParams {
  prefixMode?: "all" | "selected" | "none";
  prefixIds?: string[];
  startDate: string;
  endDate: string;
  dateBasis: AccessionLogDateBasis;
  reportRunId?: string;
}

export interface AccessionLogRow {
  caseId: string;
  accessionNumber: string;
  patientName: string;
  physicianName: string;
  collectedDate: string;
  receivedDate: string;
  specimens: string[];
  pathologistName: string;
}

export interface AccessionLogResponse extends AccessionLogParams {
  prefixFilterLabel?: string;
  generatedAt: string;
  rows: AccessionLogRow[];
  totalCases: number;
  totalParts: number;
}

export function accessionLogHttpParams(params: AccessionLogParams) {
  return { ...params, prefixIds: params.prefixIds?.join(",") };
}

function billingReportHttpParams<
  T extends { accountTypes: BillingReportAccountType[] }
>(params: T): Omit<T, "accountTypes"> & { accountTypes: string } {
  return { ...params, accountTypes: params.accountTypes.join(",") };
}

// ─── Cancer Cases ─────────────────────────────────────────────────────────────

export interface CancerCasesParams {
  startDate: string;
  endDate: string;
  page?: number;
  pageSize?: number;
}

export interface CancerCaseRow {
  caseId: string;
  caseNumber: string;
  patientName: string;
  cancerIdentifiedAt: string;
  urgencyLevel: "routine" | "urgent" | "critical";
  malignancyType: string | null;
}

export interface CancerCasesReportResponse {
  totalCount: number;
  startDate: string;
  endDate: string;
  data: CancerCaseRow[];
  pagination: PaginationMeta;
}

// ─── Billing Status ───────────────────────────────────────────────────────────

export type BillingStatusValue =
  | "pending"
  | "submitting"
  | "submitted"
  | "billed"
  | "rejected"
  | "error"
  | "reconciliation_required";

export interface BillingStatusParams {
  startDate: string;
  endDate: string;
  billingStatus?: BillingStatusValue;
  insurancePlanTypeId?: string;
  billingScenarioId?: string;
  practiceId?: string;
  prefixId?: string;
  physicianId?: string;
  caseStatusId?: string;
  page?: number;
  pageSize?: number;
}

export interface BillingStatusSummary {
  pending: number;
  submitting: number;
  submitted: number;
  billed: number;
  rejected: number;
  error: number;
  reconciliation_required: number;
}

export interface BillingStatusCaseRow {
  caseId: string;
  caseNumber: string;
  patientName: string;
  practiceName: string | null;
  billingStatus: BillingStatusValue;
  billingRejectionReason: string | null;
  billingActivityAt: string | null;
  billedAt: string | null;
  tebraEncounterId: string | null;
  billingSubmissionMethod?: "manual" | "api" | null;
}

export interface BillingStatusReportResponse {
  summary: BillingStatusSummary;
  data: BillingStatusCaseRow[];
  pagination: PaginationMeta;
}

// ─── Manual Tebra Entry ──────────────────────────────────────────────────────

export type ManualTebraReadiness =
  | "ready"
  | "legacy_blocked"
  | "blocked"
  | "all";
export type ManualTebraStatus =
  | "pending"
  | "error"
  | "rejected"
  | "reconciliation_required"
  | "submitted"
  | "billed"
  | "all";

export interface ManualTebraEntryParams {
  startDate?: DateOnlyString;
  endDate?: DateOnlyString;
  readiness?: ManualTebraReadiness;
  status?: ManualTebraStatus;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface ManualTebraAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
}

export interface ManualTebraPatient {
  id: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  dob: string | null;
  sex: string | null;
  mrn: string | null;
  phone?: string | null;
  homePhone?: string | null;
  mobilePhone?: string | null;
  workPhone?: string | null;
  email?: string | null;
  address?: ManualTebraAddress | null;
}

export interface ManualTebraInsurance {
  providerId: string | null;
  providerName: string | null;
  planTypeId: string | null;
  planTypeName: string | null;
  policyNumber: string | null;
  groupNumber: string | null;
  groupName?: string | null;
  phone?: string | null;
  address?: ManualTebraAddress | null;
  authorizationNumber?: string | null;
  relationship: string | null;
  companyId: string | null;
  planId: string | null;
  subscriberName?: string | null;
  subscriberFirstName?: string | null;
  subscriberMiddleName?: string | null;
  subscriberLastName?: string | null;
  subscriberDob?: string | null;
  subscriberSex?: string | null;
  subscriberAddress?: ManualTebraAddress | null;
}

export interface ManualTebraServiceLine {
  baseCode: string;
  modifiers: string[];
  units: number;
  unitCharge: number | null;
  totalCharge: number | null;
  diagnosisCodes: string[];
  description?: string | null;
  specimenLabels?: string[];
  source?: string | null;
}

export interface ManualTebraEntryCase {
  caseId: string;
  caseNumber: string;
  billingStatus: BillingStatusValue;
  billingSubmissionMethod: "manual" | "api" | null;
  submittedAt: string | null;
  tebraEncounterId: string | null;
  tebraPatientId: string | null;
  tebraPatientCaseId: string | null;
  reportVersion: number;
  packetHash: string;
  billingFactsSource: "signed_snapshot" | "legacy_live";
  signOffId: string | null;
  signedAt: string | null;
  dateOfService: string | null;
  billingScenario: string | null;
  directToPractice?: boolean;
  practice: { id: string; name: string } | null;
  physician: { id: string; name: string; npi: string | null } | null;
  serviceLocation?: { id: string; name: string } | null;
  destination?: {
    practiceId: string | null;
    renderingProviderId: string | null;
    renderingProviderName: string | null;
    serviceLocationId: string | null;
    serviceLocationName: string | null;
    payerScenario: string | null;
    primaryClaimType: string | null;
    encounterStatus: "Draft";
    sendPatientStatements: boolean | null;
    disableElectronicSecondary: boolean | null;
    businessTimeZone?: string | null;
  };
  patient: ManualTebraPatient | null;
  primaryInsurance: ManualTebraInsurance | null;
  secondaryInsurance: ManualTebraInsurance | null;
  authorizationNumber: string | null;
  serviceLines: ManualTebraServiceLine[];
  readiness: {
    ready: boolean;
    errors: string[];
    warnings: string[];
  };
}

export interface ManualTebraEntryReportResponse {
  summary: {
    total: number;
    ready: number;
    blocked: number;
    alreadyEntered: number;
    legacyBlocked: number;
  };
  data: ManualTebraEntryCase[];
  pagination: PaginationMeta;
}

// ─── Payables ─────────────────────────────────────────────────────────────────

export type PayableCalculationStatus =
  | "calculated"
  | "pending_confirmation"
  | "no_agreement";

export interface PayablesParams {
  startDate: string;
  endDate: string;
  practiceId?: string;
}

export interface PayableLineItem {
  label: string;
  quantity: number;
  rate: number | null;
  subtotal: number | null;
}

export interface PayableLabRow {
  practiceId: string;
  practiceName: string;
  calculationBasis: "cpt_code" | "block" | "slide";
  calculationStatus: PayableCalculationStatus;
  totalOwed: number | null;
  lineItems: PayableLineItem[];
}

export interface PayablesReportResponse {
  startDate: string;
  endDate: string;
  data: PayableLabRow[];
}

// ─── Turnaround Time ─────────────────────────────────────────────────────────

export interface TurnaroundTimeParams {
  startDate: string;
  endDate: string;
  pathologistId?: string;
  practiceId?: string;
  prefixId?: string;
  page?: number;
  pageSize?: number;
}

export interface TurnaroundTimeRow {
  caseId: string;
  caseNumber: string;
  patientName: string;
  practiceName: string | null;
  prefixCode: string | null;
  accessioningTimeMinutes: number | null;
  grossingTimeMinutes: number | null;
  embeddingTimeMinutes: number | null;
  cuttingTimeMinutes: number | null;
  reviewTimeMinutes: number | null;
  totalTatDays: number | null;
}

export interface TurnaroundTimeReportResponse {
  data: TurnaroundTimeRow[];
  pagination: PaginationMeta;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const reportsService = {
  getWorkload: (params: WorkloadParams): Promise<WorkloadReportResponse> =>
    api.get<WorkloadReportResponse>("/reports/workload", { params }),

  exportWorkload: (
    params: Omit<WorkloadParams, "page" | "pageSize">
  ): Promise<string> =>
    apiClient
      .get("/reports/workload/export", { params, responseType: "text" })
      .then((r) => r.data as string),

  getCptFrequency: (
    params: CptFrequencyParams,
    signal?: AbortSignal
  ): Promise<CptFrequencyReportResponse> =>
    api.get<CptFrequencyReportResponse>("/reports/cpt-frequency", {
      params: billingReportHttpParams(params),
      signal,
    }),

  exportCptFrequency: (
    params: Omit<CptFrequencyParams, "page" | "pageSize">
  ): Promise<string> =>
    apiClient
      .get("/reports/cpt-frequency/export", {
        params: billingReportHttpParams(params),
        responseType: "text",
      })
      .then((r) => r.data as string),

  exportCptFrequencyExcel: (
    params: Omit<CptFrequencyParams, "page" | "pageSize">
  ): Promise<Blob> =>
    apiClient
      .get("/reports/cpt-frequency/export/excel", {
        params: billingReportHttpParams(params),
        responseType: "blob",
        timeout: 120_000,
      })
      .then((response) => response.data as Blob),

  exportCptFrequencyPdf: (
    params: Omit<CptFrequencyParams, "page" | "pageSize">
  ): Promise<Blob> =>
    apiClient
      .get("/reports/cpt-frequency/export/pdf", {
        params: billingReportHttpParams(params),
        responseType: "blob",
        timeout: 120_000,
      })
      .then((response) => response.data as Blob),

  getSpecimenLog: (params: SpecimenLogParams): Promise<SpecimenLogResponse> =>
    api.get<SpecimenLogResponse>("/reports/specimen-log", {
      params: billingReportHttpParams(params),
    }),

  exportSpecimenLogExcel: (params: SpecimenLogParams): Promise<Blob> =>
    apiClient
      .get("/reports/specimen-log/export/excel", {
        params: billingReportHttpParams(params),
        responseType: "blob",
        timeout: 120_000,
      })
      .then((response) => response.data as Blob),

  exportSpecimenLogPdf: (params: SpecimenLogParams): Promise<Blob> =>
    apiClient
      .get("/reports/specimen-log/export/pdf", {
        params: billingReportHttpParams(params),
        responseType: "blob",
        timeout: 120_000,
      })
      .then((response) => response.data as Blob),

  getAccessionLog: (
    params: AccessionLogParams,
    signal?: AbortSignal
  ): Promise<AccessionLogResponse> =>
    api.get<AccessionLogResponse>("/reports/accession-log", {
      params: accessionLogHttpParams(params),
      signal,
    }),

  getAccessionLogPrefixes: (): Promise<AccessionLogPrefixOption[]> =>
    api.get("/reports/accession-log/prefixes"),

  exportAccessionLogExcel: (params: AccessionLogParams): Promise<Blob> =>
    apiClient
      .get("/reports/accession-log/export/excel", {
        params: accessionLogHttpParams(params),
        responseType: "blob",
        timeout: 120_000,
      })
      .then((response) => response.data as Blob),

  exportAccessionLogPdf: (params: AccessionLogParams): Promise<Blob> =>
    apiClient
      .get("/reports/accession-log/export/pdf", {
        params: accessionLogHttpParams(params),
        responseType: "blob",
        timeout: 120_000,
      })
      .then((response) => response.data as Blob),

  getCancerCases: (
    params: CancerCasesParams
  ): Promise<CancerCasesReportResponse> =>
    api.get<CancerCasesReportResponse>("/reports/cancer-cases", { params }),

  exportCancerCases: (
    params: Omit<CancerCasesParams, "page" | "pageSize">
  ): Promise<string> =>
    apiClient
      .get("/reports/cancer-cases/export", { params, responseType: "text" })
      .then((r) => r.data as string),

  getBillingStatus: (
    params: BillingStatusParams
  ): Promise<BillingStatusReportResponse> =>
    api.get<BillingStatusReportResponse>("/reports/billing-status", { params }),

  exportBillingStatus: (
    params: Omit<BillingStatusParams, "page" | "pageSize">
  ): Promise<string> =>
    apiClient
      .get("/reports/billing-status/export", { params, responseType: "text" })
      .then((r) => r.data as string),

  getManualTebraEntry: (
    params: ManualTebraEntryParams
  ): Promise<ManualTebraEntryReportResponse> =>
    api.get<ManualTebraEntryReportResponse>("/reports/manual-tebra-entry", {
      params,
    }),

  exportManualTebraEntry: (
    params: Omit<ManualTebraEntryParams, "page" | "pageSize">
  ): Promise<string> =>
    apiClient
      .get("/reports/manual-tebra-entry/export", {
        params,
        responseType: "text",
      })
      .then((r) => r.data as string),

  retryBilling: (
    caseId: string
  ): Promise<{
    success: boolean;
    caseId: string;
    tebraEncounterId?: string;
    tebraPatientId?: string;
    message: string;
  }> => api.post(`/billing/cases/${caseId}/retry`, {}),

  getPayables: (params: PayablesParams): Promise<PayablesReportResponse> =>
    api.get<PayablesReportResponse>("/reports/payables", { params }),

  exportPayables: (params: PayablesParams): Promise<string> =>
    apiClient
      .get("/reports/payables/export", { params, responseType: "text" })
      .then((r) => r.data as string),

  getTurnaroundTime: (
    params: TurnaroundTimeParams
  ): Promise<TurnaroundTimeReportResponse> =>
    api.get<TurnaroundTimeReportResponse>("/reports/turnaround-time", {
      params,
    }),

  exportTurnaroundTime: (
    params: Omit<TurnaroundTimeParams, "page" | "pageSize">
  ): Promise<string> =>
    apiClient
      .get("/reports/turnaround-time/export", { params, responseType: "text" })
      .then((r) => r.data as string),
};
